import { useEffect, useState } from "react";

/** Atributo que um overlay de tela cheia carrega para as novidades saberem que devem esperar. */
export const BLOCKING_OVERLAY_SELECTOR = "[data-blocking-overlay]";

/** Atributo das superfícies das próprias novidades (cartão, ponteiro): não contam como modal. */
export const SPOTLIGHT_SURFACE_ATTRIBUTE = "data-spotlight-surface";

/**
 * Um diálogo ou popover aberto que NÃO é uma superfície das novidades. O ponteiro some
 * enquanto um está aberto: o anel por cima de um modal apontaria para algo que a pessoa
 * não consegue clicar.
 */
export const OPEN_MODAL_SELECTOR = [
  `[role="dialog"][data-state="open"]:not([${SPOTLIGHT_SURFACE_ATTRIBUTE}])`,
  `[role="dialog"][aria-modal="true"]:not([${SPOTLIGHT_SURFACE_ATTRIBUTE}])`,
  '[role="alertdialog"]',
].join(", ");

const matches = (selector) => typeof document !== "undefined" && document.querySelector(selector) !== null;

/**
 * `true` enquanto existe, em qualquer lugar da página, um elemento que casa com `selector`.
 *
 * Quem bloqueia a tela se declara por um atributo, em vez de este módulo conhecer cada um
 * deles. A verificação se repete quando o DOM muda, agrupada em um quadro.
 *
 * Com `enabled` falso não observa nada: quem não tem novidade para mostrar não paga o observador.
 *
 * @param {string} selector
 * @param {boolean} [enabled]
 */
export function useDomPresence(selector, enabled = true) {
  const [present, setPresent] = useState(() => enabled && matches(selector));

  useEffect(() => {
    if (!enabled) {
      setPresent(false);
      return undefined;
    }
    let frame = 0;
    const check = () => {
      frame = 0;
      setPresent(matches(selector));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };

    check();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state", "aria-modal", "role"] });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [selector, enabled]);

  // Lido no próprio render: quando `enabled` liga (as novidades acabaram de chegar), o estado ainda
  // é o antigo até o efeito rodar, e naquele render um overlay já aberto passaria despercebido.
  return enabled && (present || matches(selector));
}
